// INTERNAL table-level lineage. Not for tenants.
//
// The table graph exposes how we model the data - staging structure, the
// intermediate models, which Gold tables back which product. That is our
// method, not a customer's own information, so this tab follows the same
// audience gate as the Alarms board: it lives in the main repo and is
// never synced to the tenant UI. There is no customer-facing lineage
// surface at all - an earlier one was built and then removed.

import { useCallback, useEffect, useState } from "react";
import { ApiHttpError, type ApiConfig, getDataLineage } from "../api";
import { TableLineageExplorer } from "./TableLineageExplorer";
import { GraphMetrics } from "./GraphMetrics";
import { TestOutcomes } from "./TestOutcomes";
import type { DataLineageResponse } from "../types";

// Poll cadence. The Silver and Gold dbt steps run for minutes, not
// seconds, so a 6s poll is well inside the resolution that matters and
// costs one cheap read. Anything faster would just burn requests.
const POLL_MS = 6000;
// States worth watching for. Anything else is resting.
const ACTIVE = new Set(["running", "failed"]);

export function InternalLineagePanel({ config }: { config: ApiConfig }) {
  const [resp, setResp] = useState<DataLineageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResp(await getDataLineage(config));
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiHttpError
          ? `${e.status} ${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live progress. The layer cards carry the run state the API derives from
  // the pipeline-events stream, so a Silver or Gold run lights up here
  // within one poll of the state machine publishing its Started event.
  useEffect(() => {
    const id = setInterval(() => {
      void load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !resp)
    return (
      <div className="lineage lin-skeleton" aria-busy="true">
        <span className="sr-only">Loading the model graph</span>
        <div className="sk sk-title" />
        <div className="sk sk-sub" />
        <div className="sk sk-band" />
      </div>
    );
  if (error) return <div className="error-banner">{error}</div>;
  if (!resp) return null;

  const { graph, stages, metrics } = resp.data;
  const runState = Object.fromEntries(stages.map((s) => [s.stage_key, s.status]));
  const active = stages.filter((s) => ACTIVE.has(s.status));

  return (
    <div className="lineage">
      <header className="lineage-head">
        <div className="lineage-masthead">
          <div>
            <h2>Model graph</h2>
            <p className="lineage-sub">
              Every table and model, with the query and tests behind each one.
            </p>
          </div>
          {/* Right-aligned: run state is a status readout, and status belongs
              where the eye lands last, not buried under the subtitle. */}
          <div className="run-strip" aria-live="polite">
            {active.length === 0 ? (
              <span className="run-pill run-idle">
                <span className="run-dot" />
                All layers idle
              </span>
            ) : (
              active.map((s) => (
                <span key={s.stage_key} className={`run-pill run-${s.status}`}>
                  <span className="run-dot" />
                  {s.display_name} {s.status === "running" ? "running now" : "failed"}
                </span>
              ))
            )}
            <span className="run-poll">live, every {POLL_MS / 1000}s</span>
          </div>
        </div>
      </header>

      {metrics ? (
        <>
          <GraphMetrics metrics={metrics} stages={stages} />
          {/* The counts above say how many; this says which, on what, and
              from where. Without it a warning count sends the operator to
              the EMR logs to find out what it was about. */}
          <TestOutcomes run={metrics.run} />
        </>
      ) : (
        <div className="gm-legacy">
          Metrics need a newer API build than the one this endpoint is running.
        </div>
      )}

      {graph ? (
        <TableLineageExplorer graph={graph} runState={runState} />
      ) : (
        <div className="empty">
          No lineage graph published yet. Run
          <code> ops/scripts/generate_lineage_graph.py --upload</code> for this tenant.
        </div>
      )}
    </div>
  );
}
